import { differenceInDays, isAfter, isBefore, sub } from 'date-fns';
import { types, flow, getEnv, getParent } from 'mobx-state-tree';
import { v4 as uuid } from 'uuid';
import { generateFee } from '@ourbranch/fee-utils';
import { advancedConnectedHomeStates, leaseLoanStates } from '@ourbranch/lookups';
import { toJS } from 'mobx';
import { isDraftablePolicyType } from 'core/helpers/policy-drafting';
import cloneDeep from 'lodash-es/cloneDeep';

import { getMerged } from 'customer/components/policy/merge-segments';
import { brandConfig } from 'brand-config';
import { types as GQLTypes } from 'core/helpers/sanitize';
import { awsDateFormatter } from 'core/helpers/formatters';
import { AgentPaymentOption, PAY_AHEAD_BILLING_HOLD_REASON, clean, isPolicyOverdue } from '@ourbranch/policy-utils';
import {
  REMOVE_HOLD_CARD,
  ADD_HOLD_CARD,
  GET_HOLD_CARDS,
  PREVIEW_POLICY_CHANGE_SCRUB_INCIDENTS,
  PREVIEW_POLICY_CHANGE_ADD_INCIDENTS,
  GET_RENEWAL_POLICY_ID,
  GET_RENEWAL_PRICE_COMPARISON,
  GET_HOME_CLAIM_HISTORY,
  CHANGE_SEGMENT,
  ADD_SEGMENT,
  CONFIRM,
  SUBMIT_CHANGES_FOR_APPROVAL,
  RECREATE_APPLICATION,
  ORDER_INSPECTION,
  GET_INSPECTION_STATUS,
  GET_CD_DIFF,
  GET_BIX_CONVERSION_SIGNATURE_FROM_TABLE,
  GET_PALOMAR_POLICY_DOC,
  REWRITE_POLICY,
  GET_POLICY_PAID_AND_PENDING,
  GET_POLICY_AUTO_PREMIUM_BREAKDOWN_PDF,
  SUBMIT_FARM_POLICY_FOR_UNDERWRITING_APPROVAL
} from 'customer/components/policy/policy.queries';
import {
  RUN_MANUAL_CHARGE,
  MODIFY_CREDIT_CARD,
  ADD_CREDIT_CARD,
  ADD_BANK_ACCOUNT,
  REMOVE_PAYMENT_METHOD,
  SEND_POLICY_INVOICE
} from 'customer/components/policy/payment-tab/payment.queries';
import { track } from 'core/helpers/analytics';
import { getPolicyStatus, PolicyStatus } from 'core/helpers/policy-status';
import { currencyFormatter, awsDateToDateFormatter } from 'core/helpers/formatters';
import { getPreviewMutationForAction } from './preview-mutation';
import Fee from './models/fee';
import PolicyEquityStatus from './models/policy-equity-status';
import BillingDetails from './models/billing-details';

export const PolicyTabs = {
  SETTINGS: 'SETTINGS',
  PAYMENT: 'PAYMENT',
  DETAILS: 'DETAILS',
  DOCUMENTS: 'DOCUMENTS',
  INCIDENTS: 'INCIDENTS'
};

export const PolicyModalTypes = {
  PREVIEW: 'PREVIEW',
  RENEWAL: 'RENEWAL',
  SUBMITTED_FOR_APPROVAL: 'SUBMITTED_FOR_APPROVAL',
  PREVIEW_LINK_EXPIRED: 'PREVIEW_LINK_EXPIRED',
  WARNING: 'WARNING',
  FARM_UW_QUESTIONS: 'FARM_UW_QUESTIONS'
};

// #35452: in-flight confirmPolicyChange mutations, keyed by `${policyId}:${previewId}`. Used to
// collapse a double-click or an Apollo/gateway retry of the same confirm into a single mutation so
// staff never trigger a second bill/refund attempt. Module-scoped (not MST state) because it holds
// live promises, which don't belong in a serializable snapshot.
const confirmInFlight = new Map();

export const PolicyStore = types
  .model({
    segments: types.array(types.frozen()),
    changed: types.optional(types.boolean, false),
    segment: types.maybeNull(types.frozen()),
    segmentId: types.maybeNull(types.string),
    policy: types.maybeNull(types.frozen()),
    loading: types.optional(types.boolean, false),
    holdCards: types.array(types.frozen()),
    holdCardLoading: types.optional(types.boolean, false),
    recreatingApplication: types.optional(types.boolean, false),
    canAddMoratorium: types.optional(types.boolean, false),
    billingDetails: types.maybeNull(BillingDetails),
    policyEquityStatus: PolicyEquityStatus,
    fees: types.array(Fee),
    writeOffTransactions: types.array(types.frozen()),
    inspection: types.optional(types.frozen()),
    bixConversionSignedTimestamp: types.maybeNull(types.string),
    palomarPolicyDecPage: types.maybeNull(types.string),
    currentTotalPaid: types.maybeNull(types.number),
    pendingAmount: types.maybeNull(types.number),
    currentTab: types.optional(types.enumeration('PolicyTabs', Object.values(PolicyTabs)), PolicyTabs.SETTINGS),
    violationsForRenewal: types.maybeNull(types.array(types.frozen())),
    scrollTo: types.maybeNull(types.string),
    branchPlusOffers: types.maybeNull(types.array(types.frozen())),
    revertPreview: types.maybeNull(types.frozen()),
    revertLoading: types.optional(types.boolean, false),
    activePolicyModal: types.maybeNull(types.enumeration('PolicyModalTypes', Object.values(PolicyModalTypes))),
    downloadingAutoPremiumBreakdown: types.optional(types.boolean, false),
    preview: types.maybeNull(types.frozen()),
    isPreviewExpired: types.maybeNull(types.boolean),
    farmNavigatorOpen: types.optional(types.boolean, false)
  })
  // Goes up by one for each revert preview request and each clearRevertPreview. A revert response is
  // used only if the number has not moved since its request started.
  // paymentResults: the latest manual-charge and refund outcome per form, kept here rather than in the
  // forms because the refresh after a charge unmounts the Payment tab. Cleared on tab change and when
  // the policy page unmounts; the billing table is the lasting record.
  .volatile(() => ({ revertRequestId: 0, paymentResults: {} }))
  .views((self) => ({
    get status() {
      return getPolicyStatus(self.policy);
    },
    get isCancelledOrInCancellation() {
      return [PolicyStatus.Cancelled, PolicyStatus.InCancellation].includes(self.status);
    }
  }))
  // fetching actions
  .actions((self) => ({
    setCurrentTab(newTab) {
      if (newTab !== self.currentTab) {
        self.paymentResults = {};
      }
      self.currentTab = newTab;
    },
    // form: 'charge' | 'refund'; result: { status, title, detail } from payment-result.js, or null to clear
    setPaymentResult(form, result) {
      self.paymentResults = {
        ...self.paymentResults,
        [form]: result && { ...result, policyId: self.policy?.id, submittedAt: Date.now() }
      };
    },
    clearPaymentResults() {
      self.paymentResults = {};
    },
    setActivePolicyModal(modalType) {
      self.activePolicyModal = modalType;
    },
    closePolicyModal() {
      self.activePolicyModal = null;
    },
    setFarmNavigatorOpen(isOpen) {
      self.farmNavigatorOpen = isOpen;
    },
    fetchHoldCards: flow(function* fetchHoldCards(policyId) {
      self.holdCardLoading = true;
      const { client } = getEnv(self);
      try {
        const {
          data: { error: holdCardsError, holdcards }
        } = yield client.query({
          query: GET_HOLD_CARDS,
          fetchPolicy: 'network-only',
          variables: {
            policyId
          }
        });
        if (holdCardsError) {
          throw new Error(holdCardsError);
        }
        self.holdCards = holdcards;
        self.canAddMoratorium = true;
      } catch (e) {
        self.holdCardLoading = false;
        throw e;
      }
    }),
    addHoldCard: flow(function* addHoldCard(reason, policyId) {
      self.holdCardLoading = true;
      const { client } = getEnv(self);
      try {
        const res = yield client.mutate({
          mutation: ADD_HOLD_CARD,
          fetchPolicy: 'no-cache',
          variables: {
            policyId,
            reason
          }
        });
        if (res.data.addHoldCard.success) {
          const { createdBy, createdDate, deleted, holdCardId } = res.data.addHoldCard;
          const cards = [...self.holdCards, { createdBy, createdDate, deleted, holdCardId, reason, policyId }];
          self.holdCards = cards;
          self.holdCardLoading = false;
        }
      } catch (e) {
        self.holdCardLoading = false;
        throw e;
      }
    }),
    removeHoldCard: flow(function* removeHoldCard(policyId, holdCardId) {
      try {
        self.holdCardLoading = true;
        const { client } = getEnv(self);
        yield client.mutate({
          mutation: REMOVE_HOLD_CARD,
          variables: { policyId, holdCardId }
        });
        const newHoldCards = self.holdCards.filter((holdCard) => holdCard.holdCardId !== holdCardId);
        self.holdCards = newHoldCards;
        self.holdCardLoading = undefined;
        return true;
      } catch (e) {
        self.holdCardLoading = undefined;
        throw e;
      }
    }),
    changeSegment: flow(function* changeSegment(
      policyId,
      segment,
      fromRevertModal = false,
      clarionDoorIdOverride = null
    ) {
      const { client } = getEnv(self);
      let revertRequestId;
      try {
        if (fromRevertModal) {
          self.revertRequestId += 1;
          revertRequestId = self.revertRequestId;
          self.revertLoading = true;
        }
        const res = yield client.mutate({
          mutation: CHANGE_SEGMENT,
          variables: {
            policyId,
            segmentId: segment.segmentId,
            segment: GQLTypes.PolicyDetailsInput(segment),
            clarionDoorIdOverride,
            fromPreviewId: self.preview?.id
          }
        });
        const preview = res.data.previewPolicyChangeToSegment;
        track('Staff Segment Change', {
          policyId,
          segmentId: segment.segmentId,
          changeType: 'edit_change',
          previewId: preview?.id,
          newPremium: preview?.newPremium,
          needsUnderwritingReview: preview?.needsUnderwritingReview
        });
        if (fromRevertModal && revertRequestId !== self.revertRequestId) {
          // The page that asked for this revert left, or a newer revert started. Drop the preview.
          // Otherwise it shows up on the next policy page, and for draftable types (CFM Auto)
          // setPreview would also swap the store's policy for the old one.
          return { ok: true, preview };
        }
        if (fromRevertModal) {
          self.revertPreview = preview;
          self.revertLoading = false;
        }

        self.setPreview(preview);
        return {
          ok: true,
          preview
        };
      } catch (err) {
        // A stale request must not stop the spinner of a newer one.
        if (fromRevertModal && revertRequestId === self.revertRequestId) {
          self.revertLoading = false;
        }
        return {
          ok: false,
          error: {
            ...err,
            networkError:
              err.networkError ||
              (err.graphQLErrors && err.graphQLErrors.find((e) => e.errorType === 'ExecutionTimeout'))
          }
        };
      }
    }),
    // #35452: de-duplicate concurrent confirms of the same preview. A double-click, or an Apollo/
    // gateway retry after a slow request, would otherwise fire confirmPolicyChange twice — the very
    // shape that double-refunded a member. The backend now guards the money movement, but collapsing
    // retries here means a second attempt is never sent: same policy+preview already in flight →
    // return the in-flight promise so both callers await the one mutation.
    confirm(policyId, previewId, internalNotes, uwReviewAnswers) {
      const inFlightKey = `${policyId}:${previewId}`;
      const existing = confirmInFlight.get(inFlightKey);
      if (existing) {
        return existing;
      }
      const request = self.runConfirm(policyId, previewId, internalNotes, uwReviewAnswers);
      confirmInFlight.set(inFlightKey, request);
      // Clear the guard on settle (resolve OR reject) so a genuine later retry can proceed.
      request.finally(() => {
        confirmInFlight.delete(inFlightKey);
      });
      return request;
    },
    runConfirm: flow(function* runConfirm(policyId, previewId, internalNotes, uwReviewAnswers) {
      const { client } = getEnv(self);
      try {
        const res = yield client.mutate({
          mutation: CONFIRM,
          variables: {
            policyId,
            previewId,
            internalNotes,
            // Farm endorsement UW answers (issue #31439) — stored on the version-history entry
            // so the documents can print them. Omitted for non-farm / no-UW changes.
            uwReviewAnswers: uwReviewAnswers ? JSON.stringify(uwReviewAnswers) : undefined
          }
        });
        if (!res.data.confirmPolicyChange.success) {
          return {
            ok: false,
            error: {
              message: ''
            }
          };
        }
        track('Staff Policy Change', { policyId, previewId, notes: internalNotes });

        // Clear preview after policy is confirmed
        self.setPreview(null);
        return {
          ok: true,
          ...res.data.confirmPolicyChange
        };
      } catch (error) {
        return {
          ok: false,
          error
        };
      }
    }),
    submitFarmPolicyForUnderwritingApproval: flow(
      function* submitFarmPolicyForUnderwritingApproval(policyId, previewId, policyVersion, answers, internalNote) {
        const { client } = getEnv(self);
        try {
          const res = yield client.mutate({
            mutation: SUBMIT_FARM_POLICY_FOR_UNDERWRITING_APPROVAL,
            variables: {
              previewId,
              policyId,
              policyVersion,
              answers: JSON.stringify(answers),
              internalNote
            }
          });
          if (!res.data.submitUWReviewForApproval.success) {
            return {
              ok: false,
              error: {
                message: 'Failed to submit farm policy for underwriting approval'
              }
            };
          }

          return {
            ok: true,
            ...res.data.submitUWReviewForApproval
          };
        } catch (error) {
          return {
            ok: false,
            error
          };
        }
      }
    ),
    submitChangesForApproval: flow(
      function* submitChangesForApproval(policyId, policyPreviewLink, internalNote, changesSummary) {
        const { client } = getEnv(self);
        try {
          const res = yield client.mutate({
            mutation: SUBMIT_CHANGES_FOR_APPROVAL,
            variables: {
              policyId,
              policyPreviewLink,
              policyChangesDescription: internalNote,
              customerName: self.primaryNamedInsured,
              changesSummary
            }
          });
          if (!res.data.sendPolicyChangesForApprovalCommunication.success) {
            return {
              ok: false,
              error: {
                message: 'Failed to submit changes for approval'
              }
            };
          }

          return {
            ok: true,
            ...res.data.sendPolicyChangesForApprovalCommunication
          };
        } catch (error) {
          return {
            ok: false,
            error
          };
        }
      }
    ),
    addSegment: flow(function* addSegment(policyId, segment, { pendingAdditionalParties } = {}) {
      const { client } = getEnv(self);
      try {
        const res = yield client.mutate({
          mutation: ADD_SEGMENT,
          variables: {
            policyId,
            segment: GQLTypes.PolicyDetailsInput(segment),
            pendingAdditionalParties
          }
        });
        const preview = res.data.previewPolicyChangeAddSegment;
        track('Staff Segment Change', {
          policyId,
          changeType: 'new_change',
          previewId: preview?.id,
          newPremium: preview?.newPremium,
          needsUnderwritingReview: preview?.needsUnderwritingReview
        });

        self.setPreview(preview);
        return {
          ok: true,
          preview
        };
      } catch (err) {
        return {
          ok: false,
          error: {
            ...err,
            networkError:
              err.networkError ||
              (err.graphQLErrors && err.graphQLErrors.find((e) => e.errorType === 'ExecutionTimeout'))
          }
        };
      }
    }),
    changePolicy: flow(function* changePolicy(policy, action) {
      const { client } = getEnv(self);
      const { mutation, previewKey, sanitize } = getPreviewMutationForAction(action);
      try {
        const res = yield client.mutate({
          mutation,
          variables: {
            // narrow the payload to the mutation's input type — cancel/reinstate take dedicated
            // inputs, so sending the whole PolicyInput would be rejected by AppSync (#31966)
            policy: sanitize(policy)
          }
        });
        const previewData = res.data[previewKey];
        const newEndDate = previewData?.diff && JSON.parse(previewData.diff).full?.addedChanges.endDate;
        track('Staff Preview Policy Change', {
          policyId: self.policy?.id,
          action,
          previewId: previewData?.id,
          newPremium: previewData?.newPremium,
          needsUnderwritingReview: previewData?.needsUnderwritingReview
        });
        return {
          ok: true,
          preview: {
            ...previewData,
            // end date can change in policy-mod if in an oden state, so return new endDate and original selected endDate
            endDate: newEndDate || policy.cancellationDate || self.policy.endDate
          },
          reasons: {
            cancelReason: policy.cancelReason,
            nonRenewReason: policy.nonRenewReason
          }
        };
      } catch (error) {
        return {
          ok: false,
          error
        };
      }
    }),
    runManualCharge: flow(
      // useSweepAccount/sweepPaymentMethodId are optional trailing args (#33155) so the
      // existing pay-ahead caller (which omits them) is unaffected.
      function* runManualCharge(
        policyId,
        accountId,
        paymentMethod,
        amount,
        internalDescription,
        billType,
        payAhead,
        useSweepAccount,
        sweepPaymentMethodId,
        requestedBy,
        agentPaymentOption
      ) {
        const { client } = getEnv(self);
        return yield client.mutate({
          mutation: RUN_MANUAL_CHARGE,
          fetchPolicy: 'no-cache',
          variables: {
            policyId,
            accountId,
            paymentMethod,
            amount,
            internalDescription,
            billType,
            payAhead,
            useSweepAccount,
            sweepPaymentMethodId,
            requestedBy,
            agentPaymentOption
          }
        });
      }
    ),
    // Staff "Send Invoice" (#34203): send the policy's invoice to the insured via the chosen
    // channel(s). Returns { success, error, letterSkippedRecentlyMailed } so the caller can toast the
    // outcome — including when the letter channel was dropped by the 7-day duplicate-mail debounce.
    // generateOnly (#36678): produce the invoice PDF for printing; nothing is emailed or mailed.
    sendPolicyInvoice: flow(function* sendPolicyInvoice({ policyId, sendEmail, sendLetter, generateOnly = false }) {
      const { client } = getEnv(self);
      const res = yield client.mutate({
        mutation: SEND_POLICY_INVOICE,
        fetchPolicy: 'no-cache',
        // withTools creates the task-run record from this id — a fresh uuid per send is correct.
        variables: { args: { policyId, sendEmail, sendLetter, generateOnly }, taskRunId: uuid() }
      });
      const result = res?.data?.toolsSendPolicyInvoice || {};
      // ToolSuccess.details is AWSJSON (a string); parse it to read the debounce flag.
      let details = {};
      try {
        details = result.details ? JSON.parse(result.details) : {};
      } catch {
        details = {};
      }
      return { ...result, letterSkippedRecentlyMailed: !!details.letterSkippedRecentlyMailed };
    }),
    addCreditCard: flow(function* addCreditCard({ accountId, policyId, basisTheoryCardToken, customerId, isPrimary }) {
      const { client } = getEnv(self);
      const {
        data: { addCreditCard: res }
      } = yield client.mutate({
        mutation: ADD_CREDIT_CARD,
        variables: {
          policyId,
          accountId,
          basisTheoryCardToken,
          customerId,
          isPrimary
        }
      });
      if (res.success) {
        self.updateCreditCards(res.card);
      }
      return res;
    }),
    changeCreditCard: flow(function* changeCreditCard({ accountId, policyId, basisTheoryCardToken, customerId }) {
      const { client } = getEnv(self);
      return yield client.mutate({
        mutation: MODIFY_CREDIT_CARD,
        variables: {
          policyId,
          accountId,
          basisTheoryCardToken,
          customerId
        }
      });
    }),
    removePaymentMethod: flow(function* removePaymentMethod(paymentMethodId, accountId, policyId) {
      const { client } = getEnv(self);
      const { data } = yield client.mutate({
        mutation: REMOVE_PAYMENT_METHOD,
        variables: {
          paymentMethodId,
          accountId,
          policyId
        }
      });
      self.billingDetails.allPaymentMethods.splice(
        self.billingDetails.allPaymentMethods.findIndex(({ id }) => id === paymentMethodId),
        1
      );
      return data;
    }),
    previewPolicyChangeScrubIncidents: flow(
      function* previewPolicyChangeScrubIncidents(policy, incidentDates, claimsToScrub) {
        const { client } = getEnv(self);
        self.loading = true;
        try {
          const res = yield client.mutate({
            mutation: PREVIEW_POLICY_CHANGE_SCRUB_INCIDENTS,
            fetchPolicy: 'no-cache',
            variables: {
              policy,
              incidentDates,
              claimsToScrub: claimsToScrub?.length ? claimsToScrub : undefined
            }
          });
          self.loading = false;
          const preview = res.data.previewPolicyChangeScrubIncidents;
          track('Staff Preview Policy Change', {
            policyId: self.policy?.id,
            action: 'scrub_incidents',
            previewId: preview?.id,
            newPremium: preview?.newPremium,
            needsUnderwritingReview: preview?.needsUnderwritingReview
          });
          return {
            ok: true,
            preview: {
              ...preview,
              endDate: self.policy.endDate
            }
          };
        } catch (e) {
          self.loading = false;
          return {
            ok: false,
            error: e
          };
        }
      }
    ),
    previewPolicyChangeAddIncidents: flow(function* previewPolicyChangeAddIncidents(policy, incidentsToAdd) {
      const { client } = getEnv(self);
      self.loading = true;
      try {
        const res = yield client.mutate({
          mutation: PREVIEW_POLICY_CHANGE_ADD_INCIDENTS,
          fetchPolicy: 'no-cache',
          variables: {
            policy,
            autoViolationsToAdd: incidentsToAdd.autoViolationsToAdd?.length
              ? incidentsToAdd.autoViolationsToAdd
              : undefined,
            homeClaimsToAdd: incidentsToAdd.homeClaimsToAdd?.length ? incidentsToAdd.homeClaimsToAdd : undefined,
            homeViolationsToAdd: incidentsToAdd.homeViolationsToAdd?.length
              ? incidentsToAdd.homeViolationsToAdd
              : undefined
          }
        });
        self.loading = false;
        const preview = res.data.previewPolicyChangeAddIncidents;
        track('Staff Preview Policy Change', {
          policyId: self.policy?.id,
          action: 'add_incidents',
          previewId: preview?.id,
          newPremium: preview?.newPremium,
          needsUnderwritingReview: preview?.needsUnderwritingReview
        });
        return { ok: true, preview: { ...preview, endDate: self.policy.endDate } };
      } catch (e) {
        self.loading = false;
        return { ok: false, error: e };
      }
    }),
    getRenewalPolicyId: flow(function* getRenewalPolicyId(policyId) {
      const { client } = getEnv(self);
      try {
        const { data } = yield client.query({
          query: GET_RENEWAL_POLICY_ID,
          variables: { policyId }
        });
        return {
          ok: true,
          renewalPolicyId: data.renewalPolicyId
        };
      } catch (error) {
        return {
          ok: false,
          error
        };
      }
    }),
    getRenewalPriceComparison: flow(function* getRenewalPriceComparison(currentPolicyId, previousPolicyId) {
      const { client } = getEnv(self);
      try {
        const { data } = yield client.query({
          query: GET_RENEWAL_PRICE_COMPARISON,
          variables: { currentPolicyId, previousPolicyId }
        });
        return {
          ok: true,
          renewalPriceComparison: data.RenewalPriceComparison
        };
      } catch (error) {
        return {
          ok: false,
          error
        };
      }
    }),
    // Earlier terms of a home lineage, read only for the Incidents tab's removed-claim history (#36608)
    getHomeClaimHistory: flow(function* getHomeClaimHistory(policyIds, accountId) {
      const { client } = getEnv(self);
      try {
        const results = yield Promise.all(
          policyIds.map((id) => client.query({ query: GET_HOME_CLAIM_HISTORY, variables: { id, accountId } }))
        );
        const terms = results.map(({ data }) => data?.policy);
        // a missing term would widen the gap a removal is judged across, so fail closed
        if (terms.some((term) => !term)) {
          return { ok: false, error: new Error('An earlier term could not be loaded') };
        }
        return { ok: true, terms };
      } catch (error) {
        return { ok: false, error };
      }
    }),
    setViolationsForRenewal: function setViolationsForRenewal(inputViolations) {
      const parsedViolations = inputViolations.map((violations) => ({
        ...violations,
        autoViolationDates: JSON.parse(violations.autoViolationDates)
      }));

      const violations = [];
      parsedViolations.forEach((record) => {
        const { autoViolationDates } = record;

        Object.keys(autoViolationDates).forEach((date) => {
          const violationsForDate = autoViolationDates[date];

          const driver = self.policy.policyDetails.drivers.find((d) => d.id === record.driverId);

          violationsForDate.forEach((violation) => {
            violations.push({
              date,
              driver: `${driver.firstName} ${driver.lastName}`,
              type: violation.class,
              source: violation.source,
              isCourtRecord: violation.isCourtRecord || false
            });
          });
        });
      });
      self.violationsForRenewal = violations;
    },
    recreateApplication: flow(function* recreateApplication(policyId) {
      const { client } = getEnv(self);
      self.recreatingApplication = true;

      try {
        const { data } = yield client.mutate({
          mutation: RECREATE_APPLICATION,
          variables: {
            policyId
          }
        });

        return data?.pdfUrl;
      } catch (error) {
        // The mutation rejects whenever RecreateApplicationLambda throws. Without this catch the
        // rejection escapes the flow unhandled, so the caller's `if (!pdfUrl)` error toast never
        // renders and the operator just sees the spinner stop.
        console.error('Failed to recreate policy application', error);
        return null;
      } finally {
        self.recreatingApplication = false;
      }
    }),
    orderInspection: flow(function* orderInspection({
      address,
      fname,
      lname,
      policyId,
      phone,
      mailingAddress,
      coverageA,
      effectiveDate
    }) {
      const { client } = getEnv(self);
      try {
        const { data } = yield client.mutate({
          mutation: ORDER_INSPECTION,
          variables: {
            address,
            fname,
            lname,
            policyId,
            phone,
            mailingAddress,
            coverageA,
            effectiveDate
          }
        });

        const { inspection } = yield self.getInspectionStatus({ policyId });
        self.inspection = inspection;

        return data;
      } catch (e) {
        self.loading = false;
        throw e;
      }
    }),
    getInspectionStatus: flow(function* getInspectionStatus({ policyId }) {
      const { client } = getEnv(self);
      try {
        const { data } = yield client.mutate({
          mutation: GET_INSPECTION_STATUS,
          variables: { policyId }
        });
        return data;
      } catch (e) {
        self.loading = false;
        throw e;
      }
    }),
    getClarionDoorRequestDiff: flow(function* getClarionDoorRequestDiff({
      policyId,
      policyPreviewId,
      cdIdBefore,
      cdIdAfter,
      invokedBy
    }) {
      const { client } = getEnv(self);
      try {
        const {
          data: { requests },
          error
        } = yield client.query({
          query: GET_CD_DIFF,
          variables: { policyId, policyPreviewId, cdIdBefore, cdIdAfter, invokedBy }
        });
        return { requests, error };
      } catch (e) {
        console.error(e);
        throw e;
      }
    }),
    getBixConversationSignatureFromTable: flow(function* getBixConversationSignatureFromTable(policyId) {
      const { client } = getEnv(self);
      try {
        const { data } = yield client.query({
          query: GET_BIX_CONVERSION_SIGNATURE_FROM_TABLE,
          variables: {
            policyId
          }
        });

        if (data?.getBixConversationSignatureFromTable?.signedTimestampISO8601) {
          self.bixConversionSignedTimestamp = data.getBixConversationSignatureFromTable.signedTimestampISO8601;
        }
      } catch (e) {
        self.loading = false;
        throw e;
      }
    }),
    fetchPolicyPaidAndPending: flow(function* getPolicyPaidAndPendingCash(policyId) {
      const { client } = getEnv(self);
      try {
        const { getPolicyPaidAndPendingCash } = yield client.query({
          query: GET_POLICY_PAID_AND_PENDING,
          fetchPolicy: 'network-only',
          variables: { policyId }
        });
        const { currentTotalPaid, pendingAmount } = getPolicyPaidAndPendingCash;

        self.currentTotalPaid = currentTotalPaid;
        self.pendingAmount = pendingAmount;
      } catch (e) {
        self.loading = false;
        throw e;
      }
    }),
    // Holds the next auto-draft until the installment after the one just paid early, so it is not
    // charged twice. The charge already went through, so a failed hold is reported for staff to set
    // manually. billingHoldReason lets the hold past the mutual-brand authority gate and maxDays cap.
    // changePolicy/confirm return { ok: false } instead of throwing, so check both.
    placePayAheadHold: flow(function* placePayAheadHold(policy, { billingHoldUntil, billingHoldReason }) {
      const changePolicyRes = yield self.changePolicy({ id: policy.id, billingHoldUntil, billingHoldReason });
      const confirmRes = changePolicyRes.ok
        ? yield self.confirm(policy.id, changePolicyRes.preview.id, 'Pay Ahead Billing Hold')
        : changePolicyRes;
      return confirmRes.ok;
    }),
    // Agent payment on the mutuals (#36683): charges the preset the agent chose; the server rejects any
    // other amount. Paying the next installment early works like Pay Ahead: a direct-bill installment
    // comes back stamped as covered (coveredBillingPeriod), otherwise the next auto-draft is held until
    // the following installment. Returns { ok, chargeSuccess, paymentMethod, last4, error }.
    runAgentPayment: flow(function* runAgentPayment(
      policy,
      { paymentMethod, sweepPaymentMethodId, preset, internalDescription }
    ) {
      try {
        const { remainingPayments, nextPayments } = self.billingDetails;
        const payAhead = preset.option === AgentPaymentOption.NextInstallment;
        const res = yield self.runManualCharge(
          policy.id,
          policy.id.slice(0, 9),
          paymentMethod,
          preset.amount,
          internalDescription,
          'feePremium',
          payAhead,
          !!sweepPaymentMethodId,
          sweepPaymentMethodId,
          undefined,
          preset.option
        );
        const result = res.data.runManualCharge;
        if (!result.success) {
          return { ok: false, chargeSuccess: false, error: result.error };
        }
        if (payAhead && !result.coveredBillingPeriod && remainingPayments > 1) {
          // No following installment date means no hold can be placed: report it rather than let the
          // next draft bill the installment just paid.
          const billingHoldUntil = nextPayments?.[1]?.date;
          const holdPlaced = billingHoldUntil
            ? yield self.placePayAheadHold(policy, {
                billingHoldUntil,
                billingHoldReason: PAY_AHEAD_BILLING_HOLD_REASON
              })
            : false;
          if (!holdPlaced) {
            return {
              ok: false,
              chargeSuccess: true,
              error: 'Payment success, but billing hold failed, please manually set'
            };
          }
        }
        return { ok: true, chargeSuccess: true, paymentMethod: result.type, last4: result.last4 };
      } catch (e) {
        return { ok: false, chargeSuccess: false, error: e?.message || String(e) };
      }
    }),
    runPayAheadManualCharge: flow(function* runPayAheadManualCharge(policy, values) {
      try {
        const {
          paymentMethod,
          amount,
          internalDescription,
          billType,
          billingHoldUntil,
          billingHoldReason,
          remainingPayments
        } = values;
        const accountId = policy.id.slice(0, 9);
        // running manual charge first, and if it fails, will error, & then the billing hold will not be set
        const manualChargeRes = yield self.runManualCharge(
          policy.id,
          accountId,
          paymentMethod,
          amount,
          internalDescription,
          billType,
          true
        );

        if (manualChargeRes.data.runManualCharge.success && remainingPayments > 1) {
          const holdPlaced = yield self.placePayAheadHold(policy, { billingHoldUntil, billingHoldReason });
          if (!holdPlaced) {
            return {
              ok: false,
              chargeSuccess: true,
              error: 'Payment success, but billing hold failed, please manually set'
            };
          }
        }

        return {
          ok: true,
          chargeSuccess: manualChargeRes.data.runManualCharge.success,
          paymentMethod: manualChargeRes.data.runManualCharge.type,
          last4: manualChargeRes.data.runManualCharge.last4
        };
      } catch (e) {
        return {
          ok: false,
          error: e
        };
      }
    }),
    // Place-on-Hold (#33568): one-click removal from the policy banner. Clears the hold date (and its
    // reason / cancellation flag) the same way the payment-tab "Cancel Hold" button does — a date one
    // month before effective, which reads as inactive — then previews + confirms the change. The
    // server clears the audit fields (placedBy/placedAt) on removal.
    removeBillingHold: flow(function* removeBillingHold() {
      const { policy } = self;
      const oneMonthBeforeEffective = sub(new Date(policy.effectiveDate), { months: 1 });
      // changePolicy/confirm swallow their own errors into { ok: false } rather than throwing, so check
      // both and throw on failure — otherwise the caller would report a false success.
      const changePolicyRes = yield self.changePolicy({
        id: policy.id,
        billingHoldUntil: awsDateFormatter(oneMonthBeforeEffective),
        billingHoldReason: null,
        billingHoldBlocksCancellations: false
      });
      if (!changePolicyRes.ok) {
        throw changePolicyRes.error || new Error('Failed to preview billing hold removal');
      }
      const confirmRes = yield self.confirm(policy.id, changePolicyRes.preview.id, 'Remove Billing Hold');
      if (!confirmRes.ok) {
        throw confirmRes.error || new Error('Failed to confirm billing hold removal');
      }
    })
  }))
  // state mutation actions
  .actions((self) => ({
    switchSegment(segmentId) {
      self.segment = getMerged(cloneDeep(toJS(self.segments)), segmentId, self.policyType);
      self.segmentId = segmentId;
      self.loading = false;
    },
    setChanged(changed) {
      self.changed = changed;
    },
    setPolicy(newPolicy) {
      self.policy = newPolicy;
      const { fees, segments } = newPolicy;
      self.fees = fees;
      const mappedSegments = segments.slice().map((s) => clean(cloneDeep(toJS(s))));
      self.segments = cloneDeep(mappedSegments);
      const currentlySelectedSegmentId = self.segment?.segmentId || mappedSegments[mappedSegments.length - 1].segmentId;
      self.segment = getMerged(cloneDeep(mappedSegments), currentlySelectedSegmentId, newPolicy.policyType);
    },
    setPreview(preview) {
      if (preview?.id && isDraftablePolicyType(self.policyType)) {
        self.preview = preview;
        self.setPolicy(preview.policy);

        const url = new URL(window.location.href);
        url.searchParams.set('previewId', preview.id);
        window.history.replaceState({}, '', url.toString());
      }
    },
    clearRevertPreview() {
      self.revertRequestId += 1;
      self.revertPreview = null;
      self.revertLoading = false;
    },
    clearPolicyPreview() {
      self.preview = null;
      self.isPreviewExpired = null;
      const url = new URL(window.location.href);
      url.searchParams.delete('previewId');
      window.history.replaceState({}, '', url.toString());
    },
    // TODO: Document options
    // TODO: Hook into formik
    addFee(type, options) {
      // Pass the brand config so config-driven fee amounts (CFM reinstatement/rewrite/NSF/late and
      // the farm/auto policy + installment fees, issue #34828) resolve to the same amounts the
      // backend charges. `brandConfig` from 'brand-config' is the inner config object, so it's wrapped
      // as { config } to match generateFee's `brandConfig.config.fees` access. Brands without a `fees`
      // block keep the legacy hardcoded amounts.
      const protoFee = generateFee(type, { ...options, brandConfig: { config: brandConfig } });
      const appliedDate = new Date().toISOString().split('T')[0];
      let description = protoFee.description;
      if (options.state === 'AL' && type === 'installment') {
        description = 'Installment (Variable, update preview to see final cost)';
      }

      self.fees.push({ ...protoFee, type, description, appliedDate, status: 'added' });
    },
    setHoldCards(cards) {
      self.holdCards = cards;
    },
    updateCreditCards(card) {
      self.billingDetails.allPaymentMethods.push(card);
    },
    replaceCreditCard(oldCard, newCard) {
      const currentIndex = self.billingDetails.allPaymentMethods.findIndex((card) => card.id === oldCard.id);
      self.billingDetails.allPaymentMethods[currentIndex] = newCard;
    },
    addBankAccount(account) {
      self.billingDetails.allPaymentMethods.push(account);
    },
    // Alternate (secondary) bank account: persists via the addBankAccount mutation — attaches the
    // account to the Stripe customer without touching the policy's primary payment method (#33527).
    addAlternateBankAccount: flow(function* addAlternateBankAccount({
      accountId,
      policyId,
      basisTheoryBankAccountToken,
      customerId,
      // Underwriter-only opt-in (issue #33938): skip MicroBilt verification for a known-good
      // account. Honored server-side only for callers with CanApproveUWReview.
      bypassMicroBiltVerification = false
    }) {
      const { client } = getEnv(self);
      const {
        data: { addBankAccount: res }
      } = yield client.mutate({
        mutation: ADD_BANK_ACCOUNT,
        variables: {
          policyId,
          accountId,
          basisTheoryBankAccountToken,
          customerId,
          bypassMicroBiltVerification
        }
      });
      if (res.success) {
        self.billingDetails.allPaymentMethods.push(res.bankAccount);
      }
      return res;
    }),
    getPalomarPolicyDoc: flow(function* getPalomarPolicyDoc(policyId, repEmail, policyType) {
      const { client } = getEnv(self);
      try {
        const { data } = yield client.query({
          query: GET_PALOMAR_POLICY_DOC,
          variables: {
            palomarPolicyId: policyId,
            repEmail,
            policyType,
            // The API serves an agent only the Palomar policy linked to a Branch policy in their book (#36672).
            policyId: self.policy?.id
          }
        });
        if (data?.palomarPolicyDoc?.palomarPolicyDecPage) {
          const downloadLink = document.createElement('a');
          downloadLink.target = '_blank';
          downloadLink.href = `data:application/pdf;base64,${data.palomarPolicyDoc.palomarPolicyDecPage}`;
          downloadLink.download = `${policyId}_Dec_Page.pdf`;
          downloadLink.click();

          return { success: true };
        }
        return { error: 'There was an error downloading Palomar Policy document' };
      } catch (error) {
        return { error };
      }
    }),
    downloadAutoPremiumBreakdownPdf: flow(function* downloadAutoPremiumBreakdownPdf(policyId, accountId, segmentId) {
      const { client } = getEnv(self);
      try {
        self.downloadingAutoPremiumBreakdown = true;

        const { data } = yield client.query({
          query: GET_POLICY_AUTO_PREMIUM_BREAKDOWN_PDF,
          variables: {
            policyId,
            accountId,
            segmentId
          },
          fetchPolicy: 'no-cache'
        });

        if (data?.getPolicyAutoPremiumBreakdownPdf) {
          window.open(data.getPolicyAutoPremiumBreakdownPdf, '_blank');
          return { success: true };
        }

        return { error: 'There was an error generating the auto premium breakdown PDF' };
      } catch (error) {
        console.error('Error downloading auto premium breakdown PDF:', error);
        return { error: error.message || 'Failed to download PDF' };
      } finally {
        self.downloadingAutoPremiumBreakdown = false;
      }
    }),
    getAllowLicensedActions(session) {
      /* 
      FormFields have this logic already built in via the permissions prop
      Therefore, only use this function if wanting to disable/enable items that are NOT form fields 
      */
      const state = self.policy?.state;
      return session.allowedStates.includes(state) && !session.viewOnly;
    },
    rewritePolicy: flow(function* rewritePolicy(policyId, newEffectiveDate, internalNotes, newPolicyId) {
      const { client } = getEnv(self);
      try {
        const { data } = yield client.mutate({
          mutation: REWRITE_POLICY,
          variables: { policyId, newEffectiveDate, internalNotes, newPolicyId }
        });
        return data.rewritePolicy;
      } catch (e) {
        return e;
      }
    }),
    setScrollTo(scrollTo) {
      self.scrollTo = scrollTo;
    },
    updateBranchPlusOffers(branchPlusOffers) {
      self.branchPlusOffers = branchPlusOffers;
    }
  }))
  .views((self) => ({
    allActiveHoldCards() {
      const cards = self.holdCards
        .slice()
        .filter((card) => !card.deleted)
        .sort((a, b) => (a.createdDate < b.createdDate ? 1 : -1));
      return cards;
    },
    reversedSegments() {
      return self.segments.slice().reverse();
    }
  }))
  .views((self) => ({
    activeHoldCards() {
      // TODO: Add argument for number of cards returned
      return self.allActiveHoldCards().slice(0, 5);
    },
    getActiveHoldCardsByReason(reason) {
      return self.allActiveHoldCards().filter((card) => card.reason === reason);
    },
    get hasBillingHold() {
      return (
        self.policy &&
        self.policy.billingHoldUntil !== null &&
        isBefore(new Date(self.policy.effectiveDate), new Date(self.policy.billingHoldUntil))
      );
    },
    get geographicState() {
      return self.policy?.state;
    },

    get policyType() {
      return self.policy?.policyType;
    },

    get premium() {
      return self.policy?.premium;
    },

    get surplusContribution() {
      return self.policy?.surplusContribution ?? 0.0;
    },

    get totalFeeCost() {
      return self.fees.reduce((total, fee) => total + fee.amount, 0);
    },

    get totalBilled() {
      return self.premium + self.surplusContribution + self.totalFeeCost;
    },

    get writeOffTotal() {
      return self.writeOffTransactions.reduce((total, writeOff) => total + writeOff.amount, 0);
    },

    get totalReceived() {
      const { transactions } = self.billingDetails;

      const paid = transactions?.filter((transaction) =>
        ['credit', 'paid', 'partially refunded', 'pending'].includes(transaction.paymentStatus)
      );

      return paid
        ? paid.reduce((total, transaction) => transaction.paymentAmount - transaction.paymentRefunded + total, 0) +
            self.writeOffTotal
        : 0;
    },

    get allTransactions() {
      const formattedWriteOffTransactions = self.writeOffTransactions.map((transaction) => ({
        id: uuid(),
        paymentDate: transaction.date,
        paymentAmount: transaction.amount,
        paymentRefunded: 0,
        paymentMethod: transaction.paymentMethod,
        paymentStatus: transaction.paymentStatus,
        internalDescription: transaction.description
      }));

      const transactions = self.billingDetails?.transactions || [];

      return [...transactions, ...formattedWriteOffTransactions];
    },

    get allTransactionsWithRefunds() {
      const refunds = self.billingDetails?.refundTransactions || [];
      return [...self.allTransactions, ...refunds].sort((a, b) => {
        const dateA = a.paymentDate || a.refundedAt || '';
        const dateB = b.paymentDate || b.refundedAt || '';
        return dateB.localeCompare(dateA);
      });
    },

    get earnedPremiumDays() {
      const { policyDays } = self.policyEquityStatus;

      const daysInForce = differenceInDays(new Date(), new Date(self.policy.effectiveDate));

      // if in the future, return 0
      if (daysInForce <= 0) {
        return 0;
      }

      return daysInForce > policyDays ? policyDays : daysInForce;
    },

    get billedPremiumDays() {
      return Math.round(self.policyEquityStatus.premiumBilled / self.policyEquityStatus.blendedDailyRate);
    },

    get paidPremiumDays() {
      return Math.round(self.policyEquityStatus.remainingAfterFees / self.policyEquityStatus.blendedDailyRate);
    },

    get paidEquityDaysRemaining() {
      const endDate = new Date(self.policy.endDate);
      const effectiveDate = new Date(self.policy.effectiveDate);

      // if in the future, 0
      if (isAfter(effectiveDate, new Date())) {
        return 0;
      }

      return isAfter(new Date(), endDate) ? 0 : self.paidPremiumDays - self.earnedPremiumDays;
    },

    get remainingPayments() {
      return self.policyEquityStatus.remainingCostBreakdown.total === 0 ? 0 : self.billingDetails.remainingPayments;
    },

    get nextPaymentAmount() {
      return self.remainingPayments > 0 ? self.billingDetails.nextPaymentAmount - self.writeOffTotal : 0;
    },

    // Total billed (amount + CC transaction fee). Falls back to amount when no fee applies.
    get nextPaymentTotal() {
      const { nextPaymentTotal, nextPaymentAmount } = self.billingDetails;
      const base = nextPaymentTotal ?? nextPaymentAmount;
      return self.remainingPayments > 0 ? base - self.writeOffTotal : 0;
    },

    get nextPaymentLabel() {
      const { nextPaymentAmount: amount } = self.billingDetails;

      return amount < 0 ? 'Refund Owed' : 'Next Payment Due';
    },

    get nextPaymentValue() {
      const { nextPaymentDate: date, nextPaymentAmount: amount, nextPaymentTotal: total } = self.billingDetails;
      const display = total ?? amount;

      const value =
        display <= 0
          ? `${currencyFormatter(Math.abs(display))}`
          : `${currencyFormatter(display)} on ${awsDateToDateFormatter(date)}`;
      return value;
    },

    get payAheadNextPayments() {
      return toJS(self.billingDetails.nextPayments).splice(1);
    },

    get isOverdue() {
      return isPolicyOverdue(null, toJS(self?.billingDetails?.transactions));
    },

    get policyId() {
      return new Promise((resolve) => {
        const checkLoading = () => {
          if (!self.loading && self.policy) {
            resolve(self.policy.id);
          } else {
            setTimeout(checkLoading, 300);
          }
        };

        checkLoading();
      });
    },
    get isBix() {
      return self.policy?.isBix;
    },
    get isAdvancedConnectedHome() {
      return self.isBix
        ? advancedConnectedHomeStates[self.geographicState]?.isBix
        : advancedConnectedHomeStates[self.geographicState]?.isNotBix;
    },
    get showLeaseLoan() {
      return self.isBix
        ? leaseLoanStates[self.geographicState]?.isBix
        : leaseLoanStates[self.geographicState]?.isNotBix;
    },
    get policyDetails() {
      if (!self.policy?.segments) {
        return null;
      }

      const lastIndex = self.policy.segments.length - 1;
      const lastSegmentId = self.policy.segments[lastIndex].segmentId;
      return getMerged(cloneDeep(toJS(self.policy.segments)), lastSegmentId, self.policy.policyType);
    },

    get primaryNamedInsured() {
      const policiesStore = getParent(self);
      const accountStore = getParent(policiesStore);

      if (accountStore.fname && accountStore.lname) {
        return `${accountStore.fname.trim()} ${accountStore.lname.trim()}`;
      }
      return undefined;
    },

    get isFarmOffer() {
      return ['F', 'Z', 'Y', 'FA'].includes(self.policyType);
    }
  }));
